import { createStore, select } from '@ngneat/elf';
import {
  withEntities,
  withActiveId,
  updateEntities,
  addEntities,
  setEntities,
  upsertEntities,
  getEntity,
  selectAllEntities
} from '@ngneat/elf-entities';

import { Injectable } from '@angular/core';
import { RestService } from 'src/app/shared/services/rest.service';
import { ContactModel } from '../models/contact.model';
import { map, switchMap, tap, distinctUntilChanged, shareReplay, take, catchError, finalize } from 'rxjs/operators';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { HelperService } from 'src/app/shared/services/helper.service';

export const store = createStore(
  { name: 'contacts' },
  withEntities<ContactModel>(),
  withActiveId()
);

@Injectable({ providedIn: 'root' })
export class ContactRepository {
  constructor(
    private readonly _restService: RestService,
    private readonly _helperService: HelperService
  ) {
  }

  private readonly _allLoaded$ = new BehaviorSubject<boolean>(false);
  private _isLoadingAll = false;
  public readonly allLoaded$ = this._allLoaded$.asObservable();
  private readonly _contactInFlight = new Map<number, Observable<ContactModel>>();

  private normalizePayload<T extends Partial<ContactModel>>(payload: T): T {
    if (!payload) return payload;

    const normalized: Partial<ContactModel> = { ...payload };

    if ('url' in payload) {
      normalized.url = this._helperService.toAbsoluteUrl(payload.url ?? null);
    }

    if (payload.birthday) {
      normalized.birthday = this._helperService.normalizeDate(new Date(payload.birthday));
    }

    return normalized as T;
  }

  getAllWhenLoaded$(): Observable<ContactModel[]> {
    return this.allLoaded$.pipe(
      distinctUntilChanged(),
      switchMap((loaded) => loaded ? store.pipe(selectAllEntities(), take(1)) : of([] as ContactModel[]))
    );
  }

  /** Alle Kontakte, laufend aktualisiert (auch nach `create`/`update`), sobald geladen. */
  selectAllLive$(): Observable<ContactModel[]> {
    return this.allLoaded$.pipe(
      distinctUntilChanged(),
      switchMap((loaded) => loaded ? store.pipe(selectAllEntities()) : of([] as ContactModel[]))
    );
  }

  /** Kontakte mit `isVeterinary`, laufend aktualisiert. */
  selectVeterinarians$(): Observable<ContactModel[]> {
    return this.selectAllLive$().pipe(map(contacts => contacts.filter(contact => !!contact.isVeterinary)));
  }

  /** Kontakte nach Id, laufend aktualisiert — für die Auflösung von `{ id }`-Referenzen. */
  selectById$(): Observable<Record<number, ContactModel>> {
    return this.selectAllLive$().pipe(
      map(contacts => Object.fromEntries(contacts.map(contact => [contact.id, contact])) as Record<number, ContactModel>)
    );
  }

  loadAllIfNeeded(): void {
    if (this._isLoadingAll || this._allLoaded$.value) return;
    this._isLoadingAll = true;
    this._restService.httpGet('/contacts').subscribe({
      next: (contacts: ContactModel[]) => {
        store.update(setEntities(contacts));
        this._allLoaded$.next(true);
      },
      error: () => {
      },
      complete: () => {
        this._isLoadingAll = false;
      }
    });
  }

  getAllOnce(): Observable<ContactModel[]> {
    return this._restService.httpGet('/contacts').pipe(
      tap((contacts: ContactModel[]) => this.setAll(contacts))
    );
  }

  getById(contactId: number | undefined): Observable<ContactModel> {
    if (contactId == null) return of({} as ContactModel);

    const cached = store.query(getEntity(contactId));
    if (cached) return of(cached);

    const pending = this._contactInFlight.get(contactId);
    if (pending) return pending;

    const request$ = this._restService.httpGet(`/contacts/${contactId}`).pipe(
      tap((fetched: ContactModel) => {
        if (fetched?.id) store.update(upsertEntities(fetched));
      }),
      map((fetched: ContactModel) => fetched ?? ({} as ContactModel)),
      catchError(() => of({} as ContactModel)),
      finalize(() => this._contactInFlight.delete(contactId)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this._contactInFlight.set(contactId, request$);
    return request$;
  }

  setAll(contacts: ContactModel[]) {
    if (!Array.isArray(contacts)) {
      console.error('Fehler: contacts ist kein Array!', contacts);
      return;
    }
    const valid = contacts.filter(c => c?.id !== undefined);
    if (!valid.length) {
      console.warn('Keine gültigen Kontakte zum Setzen gefunden.');
      return;
    }
    store.update(setEntities(valid));
    this._allLoaded$.next(true);
  }

  create(payload: Partial<ContactModel>) {
    const body = this.normalizePayload(payload);
    return this._restService.httpPost('/contacts', body)
      .pipe(tap((resp: ContactModel) => {
        if (resp?.id) {
          store.update(upsertEntities(resp));
        }
      }));
  }

  searchContacts(searchTerm: string): Observable<ContactModel[]> {
    return this._restService.httpGet(`/contacts?search=${encodeURIComponent(searchTerm)}`);
  }

  update(payload: Partial<ContactModel>, contactId: number | undefined) {
    if (contactId == null) return of({} as ContactModel);

    const { id: _id, ...payloadWithoutId } = payload as ContactModel;
    const body = this.normalizePayload(payloadWithoutId);
    return this._restService.httpPut(`/contacts/${contactId}`, body)
      .pipe(tap((contact: ContactModel) => {
        if (contact?.id) {
          store.update(updateEntities(contactId, contact));
        }
      }));
  }

  mergeContacts(newContact: Partial<ContactModel>, oldContactIds: (string | number)[]): Observable<ContactModel> {
    const normalizedNewContact: Partial<ContactModel> = {
      ...newContact,
      ...(newContact.url !== undefined ? { url: this._helperService.toAbsoluteUrl(newContact.url) } : {}),
      ...(newContact.birthday ? { birthday: this._helperService.normalizeDate(new Date(newContact.birthday)) } : {})
    };

    const payload = { newContact: normalizedNewContact, oldContactIds: oldContactIds.map(String) };

    return this._restService.httpPost('/contacts/merge', payload).pipe(
      switchMap((resp: { success: boolean; newContactId: number }) => {
        if (!resp?.newContactId) throw new Error('Kein gültiger newContactId im Merge-Response');

        const merged: ContactModel = { ...(normalizedNewContact as ContactModel), id: resp.newContactId };

        store.update(state => ({
          ...state,
          ids: [...state.ids.filter(id => !oldContactIds.includes(id)), merged.id],
          entities: {
            ...Object.fromEntries(Object.entries(state.entities).filter(([id]) => !oldContactIds.includes(Number(id)))),
            [merged.id]: merged
          }
        }));

        return of(merged);
      })
    );
  }
}
